import React, { useState } from 'react';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';

/**
 * Forced password-change modal.
 *
 * Rendered globally by App.js when the authenticated client has
 * `must_change_password = true` (admin generated a temporary password).
 *
 * UX rules:
 *  - Cannot be dismissed by clicking the backdrop or pressing Escape.
 *  - No "Cancelar" / close button — the only way out is to set a new
 *    password OR to log out completely.
 *  - Inline strength rules with live checkmarks so users know what is
 *    expected before submitting.
 *  - After success, the modal unmounts automatically because the parent's
 *    `mustChangePassword` flag flips to false.
 */
const ForcePasswordChangeModal = () => {
  const { client, changePassword, logout, mustChangePassword } = useClientAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!mustChangePassword) return null;

  const rules = {
    len: next.length >= 8,
    letter: /[A-Za-z]/.test(next),
    digit: /[0-9]/.test(next),
    diff: next.length > 0 && next !== current,
    match: confirm.length > 0 && next === confirm,
  };
  const allOk = rules.len && rules.letter && rules.digit && rules.diff && rules.match;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!allOk || submitting) return;
    setSubmitting(true);
    setError('');
    const r = await changePassword(current, next);
    setSubmitting(false);
    if (!r.success) {
      setError(r.error || 'Erro ao alterar senha');
      return;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-pw-title"
      data-testid="force-password-modal"
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start gap-3">
          <div className="bg-amber-100 dark:bg-amber-900/30 rounded-full p-2.5">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 id="force-pw-title" className="text-lg font-bold text-gray-900 dark:text-white">
              Defina sua senha de acesso
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Olá <strong>{client?.first_name || client?.name || 'peregrino'}</strong>! Você está usando uma senha temporária criada pela nossa equipe. Por segurança, escolha agora uma senha pessoal antes de continuar.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3 pt-1">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Senha temporária atual</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={e => setCurrent(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Senha que você acabou de usar"
                required
                autoFocus
                data-testid="force-pw-current"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Nova senha</label>
            <div className="relative">
              <input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={e => setNext(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Mínimo 8 caracteres, letra + número"
                required
                data-testid="force-pw-new"
              />
              <button type="button" onClick={() => setShowNext(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Confirme a nova senha</label>
            <input
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Repita a nova senha"
              required
              data-testid="force-pw-confirm"
            />
          </div>

          <ul className="text-xs space-y-1 pt-1">
            {[
              ['len', 'Pelo menos 8 caracteres'],
              ['letter', 'Inclui ao menos uma letra'],
              ['digit', 'Inclui ao menos um número'],
              ['diff', 'Diferente da senha temporária'],
              ['match', 'Confirmação coincide'],
            ].map(([k, label]) => (
              <li key={k} className={`flex items-center gap-1.5 ${rules[k] ? 'text-green-700' : 'text-gray-500'}`}>
                {rules[k] ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block" />}
                {label}
              </li>
            ))}
          </ul>

          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-300 rounded text-sm text-red-700" data-testid="force-pw-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button
              type="button"
              onClick={logout}
              className="text-xs text-gray-500 hover:text-gray-700 underline self-center sm:self-auto sm:mr-auto"
              data-testid="force-pw-logout"
            >
              Sair sem alterar
            </button>
            <button
              type="submit"
              disabled={!allOk || submitting}
              className="bg-demarre-600 hover:bg-demarre-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center justify-center gap-2 min-w-[160px]"
              data-testid="force-pw-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Salvando...' : 'Salvar nova senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChangeModal;
